import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, FileText, Target, User, Heart, Upload, Download } from 'lucide-react';
import './GlobalSearch.css';

interface SearchResult {
  id: string;
  type: 'page' | 'request' | 'mission' | 'patient' | 'user' | 'import' | 'export';
  title: string;
  titleKey?: string;
  subtitle?: string;
  path: string;
  icon: string;
}

// Static page results — titleKey is resolved via t() inside the component
const PAGE_RESULTS_CONFIG: { id: string; titleKey: string; path: string; icon: string }[] = [
  { id: 'p-dashboard', titleKey: 'components.globalSearch.pages.dashboard', path: '/', icon: 'H' },
  { id: 'p-requests', titleKey: 'components.globalSearch.pages.requests', path: '/requests', icon: 'R' },
  { id: 'p-missions', titleKey: 'components.globalSearch.pages.transports', path: '/missions', icon: 'M' },
  { id: 'p-map', titleKey: 'components.globalSearch.pages.map', path: '/map', icon: 'G' },
  { id: 'p-patients', titleKey: 'components.globalSearch.pages.myPatients', path: '/my-patients', icon: 'P' },
  { id: 'p-flow', titleKey: 'components.globalSearch.pages.flowDiagram', path: '/flow', icon: 'F' },
  { id: 'p-statistics', titleKey: 'components.globalSearch.pages.statistics', path: '/statistics', icon: 'S' },
  { id: 'p-assets', titleKey: 'components.globalSearch.pages.assets', path: '/assets', icon: 'A' },
  { id: 'p-startpoints', titleKey: 'components.globalSearch.pages.startPoints', path: '/startpoints', icon: 'L' },
  { id: 'p-hospital', titleKey: 'components.globalSearch.pages.treatmentFacilities', path: '/hospital', icon: '+' },
  { id: 'p-users', titleKey: 'components.globalSearch.pages.users', path: '/users', icon: 'U' },
  { id: 'p-settings', titleKey: 'components.globalSearch.pages.settings', path: '/settings', icon: 'O' },
  { id: 'p-audit', titleKey: 'components.globalSearch.pages.auditLogs', path: '/audit-logs', icon: 'L' },
  { id: 'p-exercise', titleKey: 'components.globalSearch.pages.exerciseControl', path: '/exercise-control', icon: 'E' },
  { id: 'p-sync', titleKey: 'components.globalSearch.pages.dataSync', path: '/data-sync', icon: 'D' },
  { id: 'p-import', titleKey: 'components.globalSearch.pages.patientImport', path: '/patient-import', icon: 'I' },
  { id: 'p-notifications', titleKey: 'components.globalSearch.pages.notifications', path: '/notifications', icon: 'N' },
  { id: 'p-docs', titleKey: 'components.globalSearch.pages.documentation', path: '/documentation', icon: '?' },
];

// Import/Export quick actions — subtitle shows the source page for clarity
const ACTION_RESULTS_CONFIG: { id: string; titleKey: string; subtitleKey: string; path: string; type: 'import' | 'export' }[] = [
  // Imports
  { id: 'a-import-patients-json', titleKey: 'components.globalSearch.actions.importPatientsJson', subtitleKey: 'components.globalSearch.actions.pagePatientImport', path: '/patient-import', type: 'import' },
  { id: 'a-import-patients-excel', titleKey: 'components.globalSearch.actions.importPatientsExcel', subtitleKey: 'components.globalSearch.actions.pageRequestDetail', path: '/requests', type: 'import' },
  { id: 'a-import-users', titleKey: 'components.globalSearch.actions.importUsers', subtitleKey: 'components.globalSearch.actions.pageUsers', path: '/users', type: 'import' },
  { id: 'a-import-asset-templates', titleKey: 'components.globalSearch.actions.importAssetTemplates', subtitleKey: 'components.globalSearch.actions.pageAssetTemplates', path: '/asset-templates', type: 'import' },
  { id: 'a-import-day-templates', titleKey: 'components.globalSearch.actions.importDayTemplates', subtitleKey: 'components.globalSearch.actions.pageDayTemplates', path: '/day-templates', type: 'import' },
  // Exports
  { id: 'a-export-requests', titleKey: 'components.globalSearch.actions.exportRequests', subtitleKey: 'components.globalSearch.actions.pageRequests', path: '/requests', type: 'export' },
  { id: 'a-export-patients', titleKey: 'components.globalSearch.actions.exportPatients', subtitleKey: 'components.globalSearch.actions.pageRequestDetail', path: '/requests', type: 'export' },
  { id: 'a-export-users', titleKey: 'components.globalSearch.actions.exportUsers', subtitleKey: 'components.globalSearch.actions.pageUsers', path: '/users', type: 'export' },
  { id: 'a-export-credentials', titleKey: 'components.globalSearch.actions.exportCredentials', subtitleKey: 'components.globalSearch.actions.pageUsers', path: '/users', type: 'export' },
  { id: 'a-export-audit-excel', titleKey: 'components.globalSearch.actions.exportAuditExcel', subtitleKey: 'components.globalSearch.actions.pageAuditLogs', path: '/audit-logs', type: 'export' },
  { id: 'a-export-audit-pdf', titleKey: 'components.globalSearch.actions.exportAuditPdf', subtitleKey: 'components.globalSearch.actions.pageAuditLogs', path: '/audit-logs', type: 'export' },
];

const TYPE_ICONS: Record<string, React.ReactNode> = {
  page: <LayoutDashboard size={14} />,
  request: <FileText size={14} />,
  mission: <Target size={14} />,
  patient: <Heart size={14} />,
  user: <User size={14} />,
  import: <Upload size={14} />,
  export: <Download size={14} />,
};

function GlobalSearch() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Resolve page results with translated titles
  const getPageResults = useCallback((): SearchResult[] => {
    const pages: SearchResult[] = PAGE_RESULTS_CONFIG.map(p => ({
      id: p.id,
      type: 'page' as const,
      title: t(p.titleKey),
      titleKey: p.titleKey,
      path: p.path,
      icon: p.icon,
    }));
    const actions: SearchResult[] = ACTION_RESULTS_CONFIG.map(a => ({
      id: a.id,
      type: a.type,
      title: t(a.titleKey),
      subtitle: t(a.subtitleKey),
      path: a.path,
      icon: a.type === 'import' ? '↑' : '↓',
    }));
    return [...pages, ...actions];
  }, [t]);

  // Open with Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults(getPageResults().slice(0, 8));
      setSelectedIndex(0);
    }
  }, [isOpen, getPageResults]);

  // Search logic
  const performSearch = useCallback(async (q: string) => {
    const pageResults = getPageResults();
    if (!q.trim()) {
      setResults(pageResults.slice(0, 8));
      setLoading(false);
      return;
    }

    const lower = q.toLowerCase();

    // Filter pages
    const pageMatches = pageResults.filter(p =>
      p.title.toLowerCase().includes(lower)
    );

    // Search API for requests/missions/users
    const apiResults: SearchResult[] = [];
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Search requests
      const reqRes = await fetch(`${API_URL}/api/requests?search=${encodeURIComponent(q)}&limit=5`, { headers });
      if (reqRes.ok) {
        const reqData = await reqRes.json();
        const requests = Array.isArray(reqData) ? reqData : reqData.requests || [];
        requests.slice(0, 5).forEach((r: any) => {
          apiResults.push({
            id: `req-${r.id}`,
            type: 'request',
            title: r.requestId || r.title || 'Request',
            subtitle: `${r.fromNation || r.nation || ''} - ${r.status}`,
            path: `/requests/${r.id}`,
            icon: 'R',
          });
        });
      }

      // Search missions
      const missRes = await fetch(`${API_URL}/api/missions?search=${encodeURIComponent(q)}&limit=5`, { headers });
      if (missRes.ok) {
        const missData = await missRes.json();
        const missions = Array.isArray(missData) ? missData : missData.missions || [];
        missions.slice(0, 5).forEach((m: any) => {
          apiResults.push({
            id: `msn-${m.id}`,
            type: 'mission',
            title: m.missionId || 'Mission',
            subtitle: `${m.status} - ${m.nation || ''}`,
            path: `/missions/${m.id}`,
            icon: 'M',
          });
        });
      }

      // Search users (admin/excon only)
      if (user?.isAdmin || user?.isExcon) {
        const userRes = await fetch(`${API_URL}/api/users?search=${encodeURIComponent(q)}&limit=5`, { headers });
        if (userRes.ok) {
          const userData = await userRes.json();
          const users = Array.isArray(userData) ? userData : userData.users || [];
          users.slice(0, 5).forEach((u: any) => {
            apiResults.push({
              id: `usr-${u.id}`,
              type: 'user',
              title: u.displayName || u.username || u.email,
              subtitle: `${u.nation} - ${u.role}`,
              path: `/users`,
              icon: 'U',
            });
          });
        }
      }
    } catch {
      // Silently fail API searches
    }

    setResults([...pageMatches, ...apiResults].slice(0, 15));
    setLoading(false);
  }, [user, getPageResults]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, query.length > 0 ? 250 : 0);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [query, performSearch]);

  const handleSelect = (result: SearchResult) => {
    setIsOpen(false);
    navigate(result.path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="global-search-overlay" onClick={() => setIsOpen(false)}>
      <div className="global-search" onClick={e => e.stopPropagation()}>
        <div className="global-search-input-wrapper">
          <svg className="global-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            className="global-search-input"
            type="text"
            placeholder={t('components.globalSearch.placeholder')}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
          />
          <kbd className="global-search-kbd">ESC</kbd>
        </div>

        <div className="global-search-results">
          {loading && results.length === 0 && (
            <div className="global-search-loading">{t('components.globalSearch.searching')}</div>
          )}
          {results.length === 0 && !loading && query && (
            <div className="global-search-empty">{t('components.globalSearch.noResults', { query })}</div>
          )}
          {results.map((result, index) => (
            <button
              key={result.id}
              className={`global-search-result ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className={`global-search-result-icon type-${result.type}`}>
                {TYPE_ICONS[result.type] || result.icon}
              </span>
              <div className="global-search-result-text">
                <span className="global-search-result-title">{result.title}</span>
                {result.subtitle && (
                  <span className="global-search-result-subtitle">{result.subtitle}</span>
                )}
              </div>
              <span className="global-search-result-type">{result.type}</span>
            </button>
          ))}
        </div>

        <div className="global-search-footer">
          <span><kbd>↑↓</kbd> {t('components.globalSearch.navigate')}</span>
          <span><kbd>Enter</kbd> {t('components.globalSearch.select')}</span>
          <span><kbd>Esc</kbd> {t('components.globalSearch.close')}</span>
        </div>
      </div>
    </div>
  );
}

export default GlobalSearch;
